/**
 * Resolved by ALICE — vulnerability exposures that went away in a month,
 * and how much of that ALICE's deployments did.
 *
 * An exposure is one CVE on one device. Both ends of the month are scored
 * against the same, current CVE data (see services/exposure_tracking.py),
 * so the figures move only when the estate does — never because NVD
 * published something. ALICE is credited only where a device is now on a
 * version ALICE rolled out, or ALICE's uninstall rollout removed the app;
 * everything else is shown alongside rather than hidden, so the number
 * stands up in a monthly report.
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { RefreshCw, Download, ShieldCheck, Info, PlayCircle } from "lucide-react";

const CARD_BG =
  "bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] " +
  "border border-[rgba(229,239,250,0.1)] rounded-xl";
const BTN_GHOST =
  "bg-[rgba(229,239,250,0.08)] border-[rgba(229,239,250,0.2)] text-white hover:bg-white/10";

const CATEGORY_META = {
  alice: { label: "Resolved by ALICE", color: "bg-emerald-400" },
  other: { label: "Resolved by other means", color: "bg-cyan-300" },
  removed_other: { label: "App removed by other means", color: "bg-slate-300" },
  device_left: { label: "Device left the estate", color: "bg-slate-500" },
};
const CATEGORY_ORDER = ["alice", "other", "removed_other", "device_left"];
const SEVERITY_META = [
  ["CRITICAL", "bg-red-600"],
  ["HIGH", "bg-orange-600"],
  ["MEDIUM", "bg-yellow-600"],
  ["LOW", "bg-blue-600"],
  ["UNSCORED", "bg-slate-500"],
];

const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
};

const KpiTile = ({ label, value, sub, tone = "default", testId }) => {
  const toneClass = { default: "text-white", good: "text-emerald-300", danger: "text-red-300" }[tone] || "text-white";
  return (
    <div className={`${CARD_BG} p-4`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-white/60">{label}</div>
      <div className={`text-2xl font-semibold leading-tight ${toneClass}`}>{value}</div>
      {sub && <div className="text-xs text-white/50 mt-1">{sub}</div>}
    </div>
  );
};

const ShareBar = ({ resolved, total }) => (
  <div>
    <div className="h-3 rounded-sm overflow-hidden bg-white/10 flex">
      {CATEGORY_ORDER.map((cat) => {
        const n = resolved?.[cat]?.exposures || 0;
        if (!total || !n) return null;
        return (
          <div
            key={cat}
            className={`h-full ${CATEGORY_META[cat].color}`}
            style={{ width: `${(n / total) * 100}%` }}
            title={`${CATEGORY_META[cat].label}: ${fmt(n)}`}
          />
        );
      })}
    </div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/70">
      {CATEGORY_ORDER.map((cat) => (
        <span key={cat} className="inline-flex items-center gap-1.5">
          <span className={`w-2.5 h-2.5 rounded-sm ${CATEGORY_META[cat].color}`} />
          {CATEGORY_META[cat].label} · {fmt(resolved?.[cat]?.exposures)}
        </span>
      ))}
    </div>
  </div>
);

const SeverityBars = ({ bySeverity }) => {
  const max = Math.max(1, ...SEVERITY_META.map(([s]) => bySeverity?.[s] || 0));
  return (
    <div className="space-y-1.5">
      {SEVERITY_META.map(([sev, color]) => (
        <div key={sev} className="flex items-center gap-2 text-xs">
          <span className="w-20 text-white/70">{sev === "UNSCORED" ? "Unscored" : sev[0] + sev.slice(1).toLowerCase()}</span>
          <div className="flex-1 h-2 bg-white/10 rounded-sm overflow-hidden">
            <div className={`h-full ${color}`} style={{ width: `${((bySeverity?.[sev] || 0) / max) * 100}%` }} />
          </div>
          <span className="w-14 text-right tabular-nums text-white/80">{fmt(bySeverity?.[sev])}</span>
        </div>
      ))}
    </div>
  );
};

const ResolvedByAliceTab = () => {
  const { getAccessToken } = useAuth();
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    return { Authorization: `Bearer ${token}` };
  }, [getAccessToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/resolved`, { headers, params: month ? { month } : {} });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the resolution report");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, month]);

  useEffect(() => { load(); }, [load]);

  const download = async () => {
    try {
      const headers = await authHeaders();
      const r = await axios.get(`${API}/cve/resolved/export`, {
        headers, params: { month: data?.month }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alice-resolved-${data?.month}.csv`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
    } catch {
      toast.error("Could not download the report");
    }
  };

  const startTracking = async () => {
    setStarting(true);
    try {
      const headers = await authHeaders();
      await axios.post(`${API}/cve/resolved/snapshot`, {}, { headers });
      toast.success("Tracking started. Figures appear after the next sync or import.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start tracking");
    } finally {
      setStarting(false);
    }
  };

  const alice = data?.resolved?.alice;
  const total = data?.resolved_total_exposures || 0;
  const introduced = data?.introduced;

  return (
    <div className="space-y-4" data-testid="resolved-by-alice-tab">
      <div className="flex flex-wrap items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-emerald-300" />
        <h2 className="text-lg font-semibold text-white">Resolved by ALICE</h2>
        <select
          className="ml-2 bg-white/10 border border-white/20 text-white text-sm rounded-md px-2 py-1"
          value={data?.month || ""}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="resolved-month-select"
        >
          {(data?.months_available?.length ? data.months_available : [data?.month]).filter(Boolean).map((m) => (
            <option key={m} value={m} className="text-black">{monthLabel(m)}</option>
          ))}
        </select>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" className={BTN_GHOST} onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" className={BTN_GHOST} onClick={download} disabled={!data}>
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>
      </div>

      {data && !data.available && (
        <div className={`${CARD_BG} p-5 text-sm text-white/80`} data-testid="resolved-unavailable">
          {data.reason === "no_snapshots" ? (
            <div className="flex flex-wrap items-center gap-3">
              <span>
                Tracking hasn&apos;t started for this tenant. ALICE records who has which version of each
                app at the end of every sync or import; the report compares two of those records.
              </span>
              <Button size="sm" onClick={startTracking} disabled={starting}>
                <PlayCircle className="w-4 h-4 mr-2" /> Start tracking now
              </Button>
            </div>
          ) : (
            <span>
              Tracking started {fmtDate(data.tracking_started_at)}. There is only one record in this
              period so far; figures appear after the next sync or import.
            </span>
          )}
        </div>
      )}

      {data?.available && (
        <>
          {data.partial && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-200">
              Tracking began part-way through {monthLabel(data.month)}, so these figures cover{" "}
              {fmtDate(data.baseline?.taken_at)} to {fmtDate(data.comparison?.taken_at)} only.
            </div>
          )}

          <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <KpiTile testId="kpi-alice-exposures" tone="good" label="Exposures resolved by ALICE"
              value={fmt(alice?.exposures)}
              sub={data.alice_share != null ? `${Math.round(data.alice_share * 100)}% of all resolved` : null} />
            <KpiTile label="Devices made safer" value={fmt(alice?.devices)} sub="at least one CVE removed" />
            <KpiTile label="Distinct CVEs resolved" value={fmt(alice?.cves)} sub={`across ${fmt(alice?.families)} apps`} />
            <KpiTile label="Critical + high" value={fmt((alice?.by_severity?.CRITICAL || 0) + (alice?.by_severity?.HIGH || 0))} sub="exposures resolved by ALICE" />
            <KpiTile label="Known exploited (KEV)" value={fmt(alice?.kev_exposures)} sub="exposures resolved by ALICE" />
            <KpiTile label="New exposures" tone={introduced?.exposures ? "danger" : "default"} value={fmt(introduced?.exposures)}
              sub={`net change ${fmt((introduced?.exposures || 0) - total)}`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={`${CARD_BG} p-4`}>
              <div className="text-sm font-semibold text-white mb-3">
                Every exposure resolved in {monthLabel(data.month)} · {fmt(total)}
              </div>
              <ShareBar resolved={data.resolved} total={total} />
              <div className="grid grid-cols-2 gap-3 mt-4 text-xs text-white/70">
                <div>Vulnerable devices: <span className="text-white">{fmt(data.estate?.vulnerable_devices_start)}</span> → <span className="text-white">{fmt(data.estate?.vulnerable_devices_end)}</span></div>
                <div>CVEs present: <span className="text-white">{fmt(data.estate?.cves_present_start)}</span> → <span className="text-white">{fmt(data.estate?.cves_present_end)}</span></div>
                <div>CVEs eliminated from the estate: <span className="text-white">{fmt(data.estate?.cves_eliminated)}</span></div>
                <div>Apps with ALICE rollouts: <span className="text-white">{fmt(data.families_with_alice_rollouts)}</span></div>
              </div>
            </div>
            <div className={`${CARD_BG} p-4`}>
              <div className="text-sm font-semibold text-white mb-3">Resolved by ALICE, by severity</div>
              <SeverityBars bySeverity={alice?.by_severity} />
              <div className="text-xs text-white/50 mt-3">
                {fmt(alice?.epss_likely_exposures)} of these had an EPSS score of 10% or more (likely to be exploited).
              </div>
            </div>
          </div>

          <div className={`${CARD_BG} p-4`}>
            <div className="text-sm font-semibold text-white mb-2">
              By app {data.families_total > data.families?.length ? `(top ${data.families.length} of ${fmt(data.families_total)})` : ""}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-white/70">App</TableHead>
                  <TableHead className="text-white/70">Versions ALICE rolled out</TableHead>
                  <TableHead className="text-white/70 text-right">By ALICE</TableHead>
                  <TableHead className="text-white/70 text-right">Devices</TableHead>
                  <TableHead className="text-white/70 text-right">Critical</TableHead>
                  <TableHead className="text-white/70 text-right">KEV</TableHead>
                  <TableHead className="text-white/70 text-right">Other means</TableHead>
                  <TableHead className="text-white/70 text-right">New</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.families || []).map((f) => (
                  <TableRow key={f.family}>
                    <TableCell className="text-white">{f.family}</TableCell>
                    <TableCell className="text-white/70 font-mono text-xs">{f.versions_rolled_out.join(", ") || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-300">{fmt(f.alice.exposures)}</TableCell>
                    <TableCell className="text-right tabular-nums text-white/80">{fmt(f.alice.devices)}</TableCell>
                    <TableCell className="text-right tabular-nums text-white/80">{fmt(f.alice.by_severity.CRITICAL)}</TableCell>
                    <TableCell className="text-right tabular-nums text-white/80">{fmt(f.alice.kev_exposures)}</TableCell>
                    <TableCell className="text-right tabular-nums text-white/60">{fmt(f.other.exposures + f.removed_other.exposures + f.device_left.exposures)}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-300">{fmt(f.introduced.exposures)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {data && (
        <div className="text-xs text-white/50 space-y-1 flex gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <div>
              An exposure is one CVE on one device. Both ends of the month are scored against the same CVE
              data (as of {fmtDate(data.cve_data_as_of)}), so only changes to the estate move these figures.
              {data.available && <> Compared: {fmtDate(data.baseline?.taken_at)} → {fmtDate(data.comparison?.taken_at)}.</>}
            </div>
            <div>{data.definitions?.alice} Test-group-only packages are not credited.</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResolvedByAliceTab;
